import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTravelCompany } from '../../../api/travel-companies/create-travel-company'
import { updateTravelCompany } from '../../../api/travel-companies/edit-travel-company'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import TravelCompanyForm from '../../../components/pages/travel-companies/travel-company-form'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TravelCompany } from '../../../lib/types/models/travel-company/travel-company'
import { TravelCompanyInput } from '../../../lib/types/models/travel-company/travel-company-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const TravelCompaniesPage = () => {
  const tableRef = React.useRef<DynamicTableRef<TravelCompany>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    TravelCompany
  >(['edit', 'create'])

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updateTravelCompany,
    onError: showAxiosError,
    onSuccess: (data) => {
      tableRef.current?.refetch()
      toast.success(`Agent de voyage "${data.name}" modifié avec succès`)
      closeModel('edit')
    }
  })

  const onSubmitEdit = async (values: TravelCompanyInput) => {
    if (!selectedData) return
    await mutateEdit({ id: selectedData.id, ...values })
  }

  const { mutateAsync } = useMutation({
    mutationFn: createTravelCompany,
    onError: showAxiosError,
    onSuccess: (data) => {
      tableRef.current?.refetch()
      toast.success(`Agent de voyage "${data.name}" créé avec succès`)
      closeModel('create')
    }
  })

  const columns: Column<TravelCompany>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true,
      cell: (data) => data.row.original.id
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'modelType',
      header: 'Modèle CSV',
      enableSorting: true,
      cell: (data) => data.row.original.modelType
    },
    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Liste des agents" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<TravelCompany>
        ref={tableRef}
        title="Liste des agents de voyage"
        deleteMessage={{
          name: 'agents',
          attributeName: 'name',
          deleteOne: (company) => `Êtes-vous sûr de vouloir supprimer l'agent "${company.name}" ?`
        }}
        columns={columns}
        selectable
        allowSearchParam
        endpoint={API.travelCompanies.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      {/* Edit */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.edit}
        title="Modifier"
        hideActions
        onCancel={() => toggleModal('edit')}
      >
        <TravelCompanyForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: onSubmitEdit
          })}
        />
      </Modal>

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <TravelCompanyForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: mutateAsync
          })}
        />
      </Modal>
    </div>
  )
}

export default TravelCompaniesPage
